import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv, faFilePdf, faChartPie } from "@fortawesome/free-solid-svg-icons";
import { saveAs } from "file-saver";
import { CSVLink } from "react-csv";

// Chart.js registration
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// ---------------------- Dummy Analytics Data ----------------------
const dummyAnalytics = {
  timeSpent: [
    { agenda: "Vendor Meeting", hours: 2 },
    { agenda: "Investor Call", hours: 1.5 },
    { agenda: "Team Sync", hours: 1 },
  ],
  outcomes: [
    { type: "Positive", count: 5 },
    { type: "Neutral", count: 2 },
    { type: "Negative", count: 1 },
  ],
  appointmentsOverTime: [
    { date: "2025-01-10", count: 2 },
    { date: "2025-01-11", count: 1 },
    { date: "2025-01-12", count: 3 },
  ],
  peopleMet: [
    { person: "Ravi Sharma", positive: 2, neutral: 1, negative: 0 },
    { person: "Anita Singh", positive: 1, neutral: 0, negative: 1 },
  ],
};

// ---------------------- Component ----------------------
const GASAnalytics = () => {
  // ---------------------- Derived KPI Metrics ----------------------
  const totalAppointments = dummyAnalytics.outcomes.reduce((sum, o) => sum + o.count, 0);
  const positiveCount = dummyAnalytics.outcomes.find(o => o.type === "Positive")?.count || 0;
  const successRate = ((positiveCount / totalAppointments) * 100).toFixed(1);
  const totalHours = dummyAnalytics.timeSpent.reduce((sum, t) => sum + t.hours, 0);
  const avgHours = (totalHours / dummyAnalytics.timeSpent.length).toFixed(1);

  // ---------------------- Chart Data ----------------------
  const barData = {
    labels: dummyAnalytics.timeSpent.map(d => d.agenda),
    datasets: [{ label: "Hours Spent", data: dummyAnalytics.timeSpent.map(d => d.hours), backgroundColor: "rgba(0,255,255,0.7)" }],
  };

  const pieData = {
    labels: dummyAnalytics.outcomes.map(d => d.type),
    datasets: [{ label: "Outcomes", data: dummyAnalytics.outcomes.map(d => d.count), backgroundColor: ["#00FFFF", "#555", "#FF3333"] }],
  };

  const lineData = {
    labels: dummyAnalytics.appointmentsOverTime.map(d => d.date),
    datasets: [{ label: "Appointments", data: dummyAnalytics.appointmentsOverTime.map(d => d.count), borderColor: "#00FFFF", backgroundColor: "rgba(0,255,255,0.2)", tension: 0.4 }],
  };

  const peopleBarData = {
    labels: dummyAnalytics.peopleMet.map(d => d.person),
    datasets: [
      { label: "Positive", data: dummyAnalytics.peopleMet.map(d => d.positive), backgroundColor: "#00FF66" },
      { label: "Neutral", data: dummyAnalytics.peopleMet.map(d => d.neutral), backgroundColor: "#AAAAAA" },
      { label: "Negative", data: dummyAnalytics.peopleMet.map(d => d.negative), backgroundColor: "#FF3333" },
    ],
  };

  // ---------------------- Chart Options ----------------------
  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { labels: { color: "#00FFFF", font: { size: 10 } } } },
    scales: { x: { ticks: { color: "#00FFFF", font: { size: 10 } } }, y: { ticks: { color: "#00FFFF", font: { size: 10 } } } },
  };

  const stackedBarOptions = { ...commonChartOptions, scales: { x: { stacked: true, ticks: { color: "#00FFFF", font: { size: 10 } } }, y: { stacked: true, ticks: { color: "#00FFFF", font: { size: 10 } } } } };
  const commonPieOptions = { maintainAspectRatio: false, plugins: { legend: { labels: { color: "#00FFFF", font: { size: 10 } } } } };

  // ---------------------- PDF / CSV Export ----------------------
  const exportPDF = () => {
    const content = `
      GAS Analytics Dashboard
      ----------------------
      Total Appointments: ${totalAppointments}
      Success Rate: ${successRate}%
      Avg Hours: ${avgHours}
    `;
    const blob = new Blob([content], { type: "application/pdf" });
    saveAs(blob, "GAS_Analytics.pdf");
  };

  const csvData = [
    ["Agenda", "Hours Spent"],
    ...dummyAnalytics.timeSpent.map(d => [d.agenda, d.hours]),
    [],
    ["Outcome Type", "Count"],
    ...dummyAnalytics.outcomes.map(o => [o.type, o.count]),
  ];

  // ---------------------- JSX ----------------------
  return (
    <div className="min-h-screen bg-black text-cyan-400 px-4 py-6">
      <h2 className="text-lg font-semibold mb-4">Analytics Dashboard</h2>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-black/40 p-3 rounded-lg border border-cyan-700 text-center">
          <div className="text-xs font-semibold text-gray-400">Total Meetings</div>
          <div className="text-xl font-bold">{totalAppointments}</div>
        </div>
        <div className="bg-black/40 p-3 rounded-lg border border-cyan-700 text-center">
          <div className="text-xs font-semibold text-gray-400">Success %</div>
          <div className="text-xl font-bold">{successRate}%</div>
        </div>
        <div className="bg-black/40 p-3 rounded-lg border border-cyan-700 text-center">
          <div className="text-xs font-semibold text-gray-400">Avg Hours</div>
          <div className="text-xl font-bold">{avgHours}</div>
        </div>
        <div className="bg-black/40 p-3 rounded-lg border border-cyan-700 text-center flex flex-col justify-center items-center">
          <div className="text-xs font-semibold text-gray-400 mb-1">Export</div>
          <div className="flex gap-2">
            <button onClick={exportPDF} className="flex items-center gap-1 px-2 py-1 border border-cyan-600 rounded text-xs">
              <FontAwesomeIcon icon={faFilePdf} /> PDF
            </button>
            <CSVLink data={csvData} filename={"GAS_Analytics.csv"} className="flex items-center gap-1 px-2 py-1 border border-cyan-600 rounded text-xs">
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </CSVLink>
          </div>
        </div>
      </div>

      {/* Insight Summary */}
      <div className="bg-black/40 p-3 rounded-lg border border-cyan-700 mb-6">
        <h3 className="text-xs font-semibold mb-2 text-cyan-300">
          <FontAwesomeIcon icon={faChartPie} className="mr-1" />
          Key Insights
        </h3>
        <ul className="text-xs list-disc list-inside text-gray-300">
          <li>Most meetings are productive ({successRate}% positive outcomes).</li>
          <li>Average meeting duration is {avgHours} hours.</li>
          <li>Vendor and Investor meetings dominate agenda; consider redistributing team time.</li>
        </ul>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-black/40 p-3 rounded-lg border border-cyan-800">
          <h3 className="text-xs font-semibold mb-1 text-cyan-300">Time Spent per Appointment</h3>
          <div className="h-[160px]"><Bar data={barData} options={commonChartOptions} /></div>
        </div>

        <div className="bg-black/40 p-3 rounded-lg border border-cyan-800">
          <h3 className="text-xs font-semibold mb-1 text-cyan-300">Outcome Distribution</h3>
          <div className="h-[160px]"><Pie data={pieData} options={commonPieOptions} /></div>
        </div>

        <div className="bg-black/40 p-3 rounded-lg border border-cyan-800 md:col-span-2">
          <h3 className="text-xs font-semibold mb-1 text-cyan-300">Appointments Over Time</h3>
          <div className="h-[180px]"><Line data={lineData} options={commonChartOptions} /></div>
        </div>

        <div className="bg-black/40 p-3 rounded-lg border border-cyan-800 md:col-span-2">
          <h3 className="text-xs font-semibold mb-1 text-cyan-300">People Met vs Outcome</h3>
          <div className="h-[200px]"><Bar data={peopleBarData} options={stackedBarOptions} /></div>
        </div>
      </div>
    </div>
  );
};

export default GASAnalytics;
